import { useState, useEffect, useRef, useCallback } from 'react'
import { fetchWithAuth, isOfflineOrUnreachableFetchError } from '@/lib/api'
import { isBasecampIntegrationTabId } from '@/lib/basecamp-tab-routing'
import { isPathKeyedUnresolvedDocumentId } from '@/components/layout/document-viewer.utils'
import {
  resolveStoragePathForFetch,
  type WorkspaceRouteInfo,
} from '@/lib/app-routes'

export interface UseExternalFileChangeDetectionOptions {
  documentId: string | null
  enabled?: boolean
  checkInterval?: number // Default: 15000ms (15 seconds)
  /** Needed to map slug browser paths (flownotes-product/...) to storage roots before by-path. */
  workspaces?: WorkspaceRouteInfo[]
  onChangeDetected?: (newModifiedAt: string) => void
  onConflictDetected?: (hasConflict: boolean) => void
}

function byPathApiUrl(documentId: string, workspaces: WorkspaceRouteInfo[] = []): string {
  if (!documentId.includes('/')) {
    return `/api/documents/${documentId}`
  }
  const storagePath = resolveStoragePathForFetch(documentId, workspaces)
  return `/api/documents/by-path?path=${encodeURIComponent(storagePath)}`
}

export interface UseExternalFileChangeDetectionReturn {
  hasExternalChanges: boolean
  lastCheckedAt: Date | null
  refreshDocument: () => Promise<void>
  dismissNotification: () => void
  isRefreshing: boolean
}

/**
 * Hook to detect external file changes (e.g., from Cursor editor)
 * Polls API periodically and checks on window focus to detect when
 * a document has been modified outside the webapp.
 */
export function useExternalFileChangeDetection({
  documentId,
  enabled = true,
  checkInterval = 15000, // 15 seconds
  workspaces = [],
  onChangeDetected,
  onConflictDetected
}: UseExternalFileChangeDetectionOptions): UseExternalFileChangeDetectionReturn {
  const workspacesRef = useRef(workspaces)
  useEffect(() => {
    workspacesRef.current = workspaces
  }, [workspaces])
  const [hasExternalChanges, setHasExternalChanges] = useState(false)
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Use refs instead of state to avoid infinite loops from dependency changes
  const lastKnownModifiedAtRef = useRef<string | null>(null)
  /** Poll timer (setTimeout chain); backs off after 5xx to reduce load when DB is down. */
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const serverErrorStreakRef = useRef(0)
  const isInitialLoadRef = useRef(true)
  const toastIdRef = useRef<string | null>(null)
  const lastWebappWriteRef = useRef<number | null>(null) // Track when webapp last wrote to file
  const isCheckingRef = useRef(false) // Prevent concurrent checks
  const onChangeDetectedRef = useRef(onChangeDetected) // Store callback in ref to avoid dependency issues

  // Update ref when callback changes
  useEffect(() => {
    onChangeDetectedRef.current = onChangeDetected
  }, [onChangeDetected])

  /**
   * Check if file has been modified externally
   * Uses refs to avoid dependency chain issues that cause infinite loops
   */
  const checkForExternalChanges = useCallback(async () => {
    if (!documentId || !enabled || isCheckingRef.current) return

    // Skip non-document views and integration tabs (no API document endpoint)
    if (documentId === 'home' ||
        documentId === 'inbox' ||
        documentId.startsWith('inbox-thread-') ||
        documentId.startsWith('tab-') ||
        documentId.startsWith('explorer-') ||
        documentId === 'flows' ||
        documentId === 'list' ||
        documentId === 'search-view' ||
        documentId === 'people-index' ||
        documentId.startsWith('person-view-') ||
        isPathKeyedUnresolvedDocumentId(documentId) ||
        documentId.startsWith('new-') ||
        documentId.startsWith('workspace-home-') ||
        isBasecampIntegrationTabId(documentId) ||
        documentId.startsWith('integration-') ||
        documentId.startsWith('gdrive-preview-')) {
      return
    }

    isCheckingRef.current = true
    try {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return
      }
      const response = await fetchWithAuth(byPathApiUrl(documentId, workspacesRef.current))
      if (!response.ok) {
        if (response.status >= 500 && response.status !== 429) {
          serverErrorStreakRef.current = Math.min(5, serverErrorStreakRef.current + 1)
        } else {
          serverErrorStreakRef.current = 0
        }
        if (response.status < 500 && response.status !== 404) {
          console.warn('[ExternalFileChange] Failed to check for changes:', response.statusText)
        }
        return
      }
      serverErrorStreakRef.current = 0

      const doc = await response.json()
      // Cloud PUT updates updated_at; file_modified_at is often only set on filesystem sync.
      const currentModifiedAt =
        doc.file_modified_at ||
        doc.fileModifiedAt ||
        doc.updated_at ||
        doc.updatedAt ||
        null

      // On initial load, just store the timestamp
      if (isInitialLoadRef.current) {
        if (currentModifiedAt) {
          lastKnownModifiedAtRef.current = currentModifiedAt
          isInitialLoadRef.current = false
        }
        setLastCheckedAt(new Date())
        return
      }

      // Compare timestamps using ref (avoids dependency issues)
      const lastKnown = lastKnownModifiedAtRef.current
      if (currentModifiedAt && lastKnown) {
        const currentTime = new Date(currentModifiedAt).getTime()
        const lastTime = new Date(lastKnown).getTime()
        const timeDiff = currentTime - lastTime

        // Skip if webapp just wrote to file (within last 10 seconds)
        // Increased grace period to account for auto-save delays and file watcher sync time
        const WEBAPP_WRITE_GRACE_MS = 10000 // 10 seconds
        const now = Date.now()
        const timeSinceWebappWrite = lastWebappWriteRef.current
          ? now - lastWebappWriteRef.current
          : Infinity

        // External change detected (file modified more recently than our last known timestamp)
        // Add grace period: ignore changes within last 5 seconds (likely from file watcher sync or auto-save)
        // Also ignore changes within 10 seconds of webapp write (webapp-initiated changes)
        const GRACE_PERIOD_MS = 5000 // 5 seconds
        const isRecentChange = timeDiff < GRACE_PERIOD_MS
        const isWebappWrite = timeSinceWebappWrite < WEBAPP_WRITE_GRACE_MS

        // Only trigger notification if:
        // 1. File is actually newer (timeDiff > 0)
        // 2. Change is not within grace period (allows for file watcher sync delays)
        // 3. Change is not from webapp write (prevents false positives from auto-save)
        // 4. Time difference is significant enough (> 2 seconds) to avoid race conditions
        const MIN_TIME_DIFF_MS = 2000 // Require at least 2 seconds difference
        if (timeDiff > MIN_TIME_DIFF_MS && !isRecentChange && !isWebappWrite) {
          console.log('[ExternalFileChange] External change detected', {
            documentId,
            lastKnown,
            current: currentModifiedAt,
            timeDiffSeconds: Math.round(timeDiff / 1000),
            timeSinceWebappWrite: timeSinceWebappWrite < Infinity ? `${Math.round(timeSinceWebappWrite / 1000)}s` : 'never',
            minTimeDiff: MIN_TIME_DIFF_MS / 1000
          })

          setHasExternalChanges(true)
          lastKnownModifiedAtRef.current = currentModifiedAt // Update ref to prevent duplicate notifications
          onChangeDetectedRef.current?.(currentModifiedAt) // Use ref to call callback
        } else if ((isRecentChange || isWebappWrite) && timeDiff > 0) {
          // Recent change within grace period or from webapp write - update timestamp but don't notify
          // Silently update - no logging to reduce console noise
          lastKnownModifiedAtRef.current = currentModifiedAt
        }
      } else if (currentModifiedAt && !lastKnown) {
        // First check after reset - just store the timestamp
        lastKnownModifiedAtRef.current = currentModifiedAt
      }

      setLastCheckedAt(new Date())
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
      if (error instanceof Error && error.name === 'AbortError') {
        return
      }
      serverErrorStreakRef.current = Math.min(5, serverErrorStreakRef.current + 1)
      if (!isOfflineOrUnreachableFetchError(error)) {
        console.error('[ExternalFileChange] Error checking for changes:', error)
      }
    } finally {
      isCheckingRef.current = false
    }
  }, [documentId, enabled]) // Removed onChangeDetected from dependencies - using ref instead

  /**
   * Refresh document by triggering a custom event
   * DocumentContent component will listen for this event and reload
   */
  const refreshDocument = useCallback(async () => {
    if (!documentId || isRefreshing) return

    setIsRefreshing(true)
    try {
      // Mark that webapp will be updating the file (prevent false positive detection)
      lastWebappWriteRef.current = Date.now()

      // Dispatch custom event that DocumentContent can listen to
      window.dispatchEvent(new CustomEvent('document-refresh-requested', {
        detail: { documentId }
      }))

      // Wait for refresh to complete and file watcher to sync (2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Fetch updated document to get new file_modified_at
      try {
        const response = await fetchWithAuth(byPathApiUrl(documentId, workspacesRef.current))
        if (response.ok) {
          const doc = await response.json()
          const currentModifiedAt =
            doc.file_modified_at ||
            doc.fileModifiedAt ||
            doc.updated_at ||
            doc.updatedAt
          if (currentModifiedAt) {
            lastKnownModifiedAtRef.current = currentModifiedAt
          }
        }
      } catch (err) {
        console.warn('[ExternalFileChange] Failed to update timestamp after refresh:', err)
      }

      // Clear the notification after refresh
      setHasExternalChanges(false)
      setLastCheckedAt(new Date())
    } catch (error) {
      console.error('[ExternalFileChange] Error refreshing document:', error)
    } finally {
      setIsRefreshing(false)
    }
  }, [documentId, isRefreshing])

  /**
   * Dismiss the notification without refreshing
   */
  const dismissNotification = useCallback(async () => {
    setHasExternalChanges(false)
    // Update last known timestamp to current to prevent immediate re-notification
    if (documentId) {
      try {
        const res = await fetchWithAuth(byPathApiUrl(documentId, workspacesRef.current))
        if (res.ok) {
          const doc = await res.json()
          const currentModifiedAt =
            doc.file_modified_at ||
            doc.fileModifiedAt ||
            doc.updated_at ||
            doc.updatedAt
          if (currentModifiedAt) {
            lastKnownModifiedAtRef.current = currentModifiedAt
          }
        }
      } catch (err) {
        console.warn('[ExternalFileChange] Failed to update timestamp after dismiss:', err)
      }
    }
  }, [documentId])

  // Set up polling and window focus detection (setTimeout chain with 5xx backoff)
  useEffect(() => {
    if (!documentId || !enabled) {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current)
        pollTimeoutRef.current = null
      }
      return
    }

    void checkForExternalChanges()

    const handleFocus = () => {
      void checkForExternalChanges()
    }

    const base = checkInterval
    const schedulePoll = (delay: number) => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = setTimeout(async () => {
        pollTimeoutRef.current = null
        if (!document.hidden) {
          await checkForExternalChanges()
        }
        const streak = serverErrorStreakRef.current
        const mult = streak > 0 ? Math.min(8, 2 ** Math.min(streak, 4)) : 1
        const nextDelay = Math.min(120_000, Math.max(base, base * mult))
        schedulePoll(nextDelay)
      }, delay)
    }

    schedulePoll(base)

    window.addEventListener('focus', handleFocus)
    const onOnline = () => {
      void checkForExternalChanges()
    }
    window.addEventListener('online', onOnline)

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('online', onOnline)
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current)
        pollTimeoutRef.current = null
      }
    }
  }, [documentId, enabled, checkInterval, checkForExternalChanges])

  // Reset when document changes
  useEffect(() => {
    setHasExternalChanges(false)
    lastKnownModifiedAtRef.current = null
    isInitialLoadRef.current = true
    setLastCheckedAt(null)
    lastWebappWriteRef.current = null
    serverErrorStreakRef.current = 0
  }, [documentId])

  // Listen for webapp write events to track when webapp modifies files
  useEffect(() => {
    if (!documentId) return

    const handleDocumentWrite = (event: Event) => {
      const customEvent = event as CustomEvent
      const writtenDocId = customEvent.detail?.documentId

      // If webapp wrote to this document, mark the timestamp
      if (writtenDocId === documentId) {
        lastWebappWriteRef.current = Date.now()

        // Also update lastKnownModifiedAt to prevent false positives
        if (customEvent.detail?.fileModifiedAt) {
          lastKnownModifiedAtRef.current = customEvent.detail.fileModifiedAt
        } else {
          // Fetch current timestamp after a short delay (file watcher needs time to sync)
          setTimeout(() => {
            fetchWithAuth(byPathApiUrl(documentId, workspacesRef.current))
              .then(res => res.json())
              .then(doc => {
                const currentModifiedAt = doc.file_modified_at || doc.fileModifiedAt
                if (currentModifiedAt) {
                  lastKnownModifiedAtRef.current = currentModifiedAt
                }
              })
              .catch(err => console.warn('[ExternalFileChange] Failed to update timestamp after webapp write:', err))
          }, 1000) // Wait 1 second for file watcher to sync
        }
      }
    }

    window.addEventListener('document-written-by-webapp', handleDocumentWrite)
    return () => {
      window.removeEventListener('document-written-by-webapp', handleDocumentWrite)
    }
  }, [documentId])

  // After MCP (or other) content bumps, re-check this document without waiting for the poll interval
  useEffect(() => {
    if (!documentId || !enabled) return
    let debounceTimer: ReturnType<typeof setTimeout> | null = null
    const onRevision = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(() => {
        checkForExternalChanges()
      }, 400)
    }
    window.addEventListener('flownotes-content-revision', onRevision)
    return () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      window.removeEventListener('flownotes-content-revision', onRevision)
    }
  }, [documentId, enabled, checkForExternalChanges])

  return {
    hasExternalChanges,
    lastCheckedAt,
    refreshDocument,
    dismissNotification,
    isRefreshing
  }
}


